import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    useGenerarPdfConvocatoriaMutation,
    useGenerarPdfResultadosConvocatoriaMutation,
    useGetConvocatoriasQuery
} from '../../../services/periodicosApi';
import useGlobalFilters from '../../../hooks/useGlobalFilters';
import { enqueueSnackbar } from 'notistack';
import { useAppDispatch } from '../../../hooks/reduxHooks';
import { showAlert } from '../../../features/slices/applicationSlice';

export const useConvocatorias = () => {
    const {
        clearFilters,
        extraFilters,
        filters,
        setFilters,
        setExtraFilters,
        handleSubmitSearch,
        handleChangeExtraFilters: handleGlobalChangeExtraFilters
    } = useGlobalFilters();
    const [triggerSearch, setTriggerSearch] = useState(false);
    const [generarPdfConvocatorias] = useGenerarPdfConvocatoriaMutation();
    const [isGeneratingConvocatoriaPdf, setIsGeneratingConvocatoriaPdf] = useState(false);
    const [isGeneratingResultadosPdf, setIsGeneratingResultadosPdf] = useState(false);
    const [generarPdfResultadosConvocatoria] = useGenerarPdfResultadosConvocatoriaMutation();
    const {
        data: convocatoriasData,
        isFetching,
        refetch
    } = useGetConvocatoriasQuery(filters, { skip: !triggerSearch });
    const dispatch = useAppDispatch();
    useEffect(() => {
        setTriggerSearch(true);
    }, [filters]);

    const handleReload = () => {
        refetch();
    };

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedConvocatoria, setSelectedConvocatoria] = useState<any>(null);
    const menuOpen = Boolean(anchorEl);
    const navigate = useNavigate();

    const handleClickAction = (event: React.MouseEvent<HTMLElement>, item: any) => {
        setAnchorEl(event.currentTarget);
        setSelectedConvocatoria(item);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
        setSelectedConvocatoria(null);
    };

    const handleGeneratePdfConvocatorias = async (idConvocatoria: number) => {
        handleCloseMenu();
        setIsGeneratingConvocatoriaPdf(true);
        try {
            const pdfResponse = await generarPdfConvocatorias(idConvocatoria).unwrap();

            const base64Data = pdfResponse?.data;
            const byteCharacters = atob(base64Data);
            const byteNumbers = Array.from(byteCharacters, (char) => char.charCodeAt(0));
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: 'application/pdf' });

            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `Detalle_convocatoria_${idConvocatoria}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);

            enqueueSnackbar('PDF generado correctamente.', { variant: 'success' });
            handleReload();
        } catch (error: any) {
            enqueueSnackbar(error?.data?.message || 'Ocurrió un error al generar el PDF', {
                variant: 'error'
            });
        } finally {
            setIsGeneratingConvocatoriaPdf(false);
        }
    };

    const handleGeneratePdfResultados = async (idConvocatoria: number) => {
        handleCloseMenu();
        setIsGeneratingResultadosPdf(true);
        try {
            const pdfResponse = await generarPdfResultadosConvocatoria(idConvocatoria).unwrap();

            const base64Data = pdfResponse?.data;
            const byteCharacters = atob(base64Data);
            const byteNumbers = Array.from(byteCharacters, (char) => char.charCodeAt(0));
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: 'application/pdf' });

            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `Resultados_convocatoria_${idConvocatoria}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);

            enqueueSnackbar('PDF de resultados generado correctamente.', {
                variant: 'success'
            });
            handleReload();
        } catch (error: any) {
            const mensajeError = error?.data?.message;
            const statusCode = error?.status;

            if (statusCode === 400) {
                dispatch(
                    showAlert({
                        title: 'No se pudo generar el archivo ',
                        message:
                            'Aun existen empleados sin resultados. Por favor completá la información para poder generar el archivo.',
                        icon: 'error'
                    })
                );
            } else {
                enqueueSnackbar(
                    mensajeError || 'Ocurrió un error al generar el PDF de resultados',
                    { variant: 'error' }
                );
            }
        } finally {
            setIsGeneratingResultadosPdf(false);
        }
    };
    const handleGoToConvocados = () => {
        if (selectedConvocatoria?.id_convocatoria) {
            navigate(`./convocados`, {
                state: {
                    idConvocatoria: selectedConvocatoria.id_convocatoria,
                    fechaAlta: selectedConvocatoria.fecha_alta,
                    idOrganismoCentral: selectedConvocatoria.organismoCentral?.id,
                    cuit: selectedConvocatoria.organismoCentral?.cuit,
                    nombreOrganismoCentral: selectedConvocatoria.organismoCentral?.nombre
                }
            });
        }
    };

    return {
        filters,
        setFilters,
        handleSubmitSearch,
        convocatoriasData,
        isFetching,
        handleReload,
        clearFilters,
        extraFilters,
        handleClickAction,
        anchorEl,
        menuOpen,
        selectedConvocatoria,
        handleCloseMenu,
        handleGoToConvocados,
        handleChangeExtraFilters: handleGlobalChangeExtraFilters,
        handleGeneratePdfConvocatorias,
        handleGeneratePdfResultados,
        isGeneratingConvocatoriaPdf,
        isGeneratingResultadosPdf
    };
};
